/**
 * Mirror of MANAGER_TO_UNIT in
 * src/modules/metrics/base_unit_registry.py
 * (each metric class's `BASE_UNIT` declaration).
 *
 * Source of truth lives on the BE. Keep in sync until the transport
 * metrics view ships and projects `units` server-side, at which point
 * this file should be deleted in favor of consuming the BE-derived
 * field directly from the metrics resource.
 */
const MANAGER_TO_UNIT: Record<string, string> = {
  audio_duration_metric_manager: "s",
  interruption_rate_manager: "per_minute",
  manager_abrupt_pitch_changes: "per_minute",
  manager_agent_fails_to_respond: "binary",
  manager_agent_needs_reprompting: "count",
  manager_agentic_llm_judge_categorical: "categorical",
  manager_agentic_llm_judge_numerical: "score",
  manager_agent_repeats_itself: "binary",
  manager_agent_refusal: "binary",
  manager_api_state: "match",
  manager_audio_frequency: "percent",
  manager_audio_llm_binary: "binary",
  manager_audio_llm_categorical: "categorical",
  manager_audio_llm_numerical: "score",
  manager_audio_sentiment: "categorical",
  manager_clipping_artifact: "severity",
  manager_codec_artifact: "severity",
  manager_persona_clipping_artifact: "severity",
  manager_persona_codec_artifact: "severity",
  manager_composite_evaluation: "quality",
  manager_custom_agent_fails_to_respond: "binary",
  manager_custom_agent_needs_reprompting: "count",
  manager_custom_audio_frequency: "percent",
  manager_default_categorical: "categorical",
  manager_dropout_artifact: "per_minute",
  manager_end_reason: "categorical",
  manager_latency: "s",
  manager_llm_binary: "binary",
  manager_llm_ttfb: "s",
  manager_loop_detection: "count",
  manager_lufs: "LUFS",
  manager_match_expected_sim_wrapper: "match",
  manager_music_detection: "count",
  manager_numerical_llm_judge: "score",
  manager_persona_agent_repeats_itself: "binary",
  manager_persona_audio_sentiment: "categorical",
  manager_persona_default_categorical: "categorical",
  manager_persona_interruption_rate: "per_minute",
  manager_persona_latency: "s",
  manager_persona_llm_binary: "binary",
  manager_persona_numerical_llm_judge: "score",
  manager_persona_role_flip: "binary",
  manager_persona_transcript_regex_match: "binary",
  manager_persona_transcript_sentiment: "categorical",
  manager_phoneme_stretch: "s",
  manager_non_expressive_pauses: "per_minute",
  manager_volume_pitch_misalignment: "severity",
  manager_phonemes_per_second: "per_second",
  manager_pitch_variability: "st",
  manager_rubric_decision_tree: "score",
  manager_persona_phoneme_stretch: "s",
  manager_persona_non_expressive_pauses: "per_minute",
  manager_sentimentanalysis: "categorical",
  manager_signal_to_noise_ratio: "dB",
  manager_speaking_time_percentage: "percent",
  manager_spectrogram_pitch_analysis: "binary",
  manager_speech_artifact_anomaly: "quality",
  manager_stt_ttfb: "s",
  manager_syllable_rate: "per_second",
  manager_terminal_contour: "percent",
  manager_timbre_drift: "severity",
  manager_time_to_first_audio: "ms",
  manager_transcript_regex_match: "binary",
  manager_user_frustration: "binary",
  manager_transcription_error: "ratio",
  manager_tts_ttfb: "s",
  manager_volume: "dB",
  manager_words_per_message_with_threshold: "binary",
  manager_persona_volume_pitch_misalignment: "severity",
  manager_persona_volume: "dB",
  manager_persona_loop_detection: "count",
  manager_persona_music_detection: "count",
  pause_analysis_metric: "per_minute",
  // manager_derived_strict_logic: omitted — string output, no base unit.
  // manager_derived_aggregate: omitted — unit is inherited from parent metrics at runtime.
}

/**
 * Merges the manager-derived unit into metric_metadata when the DB row does
 * not already carry a units value. Accepts any shape that exposes `units?: string`.
 */
export function mergeManagerUnit<
  M extends { units?: string } | null | undefined,
>(row: { metric_manager: string; metric_metadata?: M }): NonNullable<M> {
  const dbMetadata = (row.metric_metadata ?? {}) as NonNullable<M>
  const managerUnit = MANAGER_TO_UNIT[row.metric_manager]
  if (dbMetadata.units || !managerUnit) {
    return dbMetadata
  }
  return { ...dbMetadata, units: managerUnit }
}
